import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWriteGuard } from '@/hooks/useWriteGuard';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Link as MUILink,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Build,
  Home,
  Inventory,
  KeyboardArrowDown,
  KeyboardArrowRight,
  Search,
  UnfoldLess,
  UnfoldMore,
} from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { type Dayjs } from 'dayjs';
import { SendForMaintenanceModal, ReceiveFromMaintenanceModal } from '@/components/maintenance';
import {
  useMaintenanceRequestLogItems,
  useMaintenanceRequestLogs,
} from '@/hooks/queries/maintenance';
import { useAllActiveCarts } from '@/hooks/queries/cart/useCartQueries';
import { useClearCart } from '@/hooks/mutations/cart/useCartMutations';
import type { WorkflowCart } from '@/services/cart';
import { usePageTitle } from '@/hooks/usePageTitle';
import { getErrorMessage } from '@/utils/httpClient';
import { multiWordMatch } from '@/utils/multiWordMatch';

type StatusFilter = 'all' | 'pending' | 'partially_received' | 'complete';
type DerivedStatus = 'Pending' | 'Partially Received' | 'Complete';
type ReceiveTarget = { maintenanceRequestId: string; documentNumber: string | null };
type MaintenanceSortField = 'documentNumber' | 'date_sent' | 'total_quantity' | 'received_quantity' | 'derivedStatus';

/**
 * Derives the display status based on received and outstanding quantities.
 */
const getDerivedStatus = (log: {
  received_quantity: number;
  outstanding_quantity: number;
  is_complete: boolean;
}): DerivedStatus => {
  if (log.is_complete) return 'Complete';
  if (log.received_quantity > 0 && log.outstanding_quantity > 0) return 'Partially Received';
  return 'Pending';
};

export const MaintenanceRequestPage: React.FC = () => {
  const navigate = useNavigate();
  usePageTitle('Maintenance Request');
  const isViewer = useWriteGuard();

  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [receiveTarget, setReceiveTarget] = useState<ReceiveTarget | null>(null);
  const [cancelCart, setCancelCart] = useState<WorkflowCart | null>(null);
  const [newSendConflictOpen, setNewSendConflictOpen] = useState(false);
  const [isResolvingNewSend, setIsResolvingNewSend] = useState(false);

  const logsQuery = useMaintenanceRequestLogs();
  const logs = useMemo(() => logsQuery.data ?? [], [logsQuery.data]);

  const { data: allActiveCarts = [], isLoading: isCartsLoading } = useAllActiveCarts();
  const clearCartMutation = useClearCart();

  const savedMaintenanceCarts = useMemo(
    () => allActiveCarts.filter(
      (c) => (c.workflow_type === 'maintenance-send' || c.workflow_type === 'maintenance-receive')
             && (c.item_count ?? 0) > 0,
    ),
    [allActiveCarts],
  );

  const savedMaintenanceSendCart = useMemo(
    () => savedMaintenanceCarts.find((cart) => cart.workflow_type === 'maintenance-send') ?? null,
    [savedMaintenanceCarts],
  );

  const handleOpenNewSend = useCallback(() => {
    if (savedMaintenanceSendCart) {
      setNewSendConflictOpen(true);
      return;
    }
    setSendModalOpen(true);
  }, [savedMaintenanceSendCart]);

  const handleStartNewSend = useCallback(async () => {
    if (!savedMaintenanceSendCart?.customer_id || isResolvingNewSend) {
      return;
    }

    setIsResolvingNewSend(true);
    try {
      const result = await clearCartMutation.mutateAsync({
        customerId: savedMaintenanceSendCart.customer_id,
        cartId: savedMaintenanceSendCart.id,
        workflowType: 'maintenance-send',
      });

      if (result.success) {
        setNewSendConflictOpen(false);
        setSendModalOpen(true);
      }
    } catch {
      // Errors handled by mutation notifications
    } finally {
      setIsResolvingNewSend(false);
    }
  }, [savedMaintenanceSendCart, isResolvingNewSend, clearCartMutation]);

  const handleCancelSavedCart = useCallback(async () => {
    if (!cancelCart?.customer_id) return;
    try {
      const result = await clearCartMutation.mutateAsync({
        customerId: cancelCart.customer_id,
        cartId: cancelCart.id,
        workflowType: cancelCart.workflow_type,
      });
      if (result.success) {
        setCancelCart(null);
      }
    } catch {
      // Errors handled by mutation notifications
    }
  }, [cancelCart, clearCartMutation]);

  const [documentSearch, setDocumentSearch] = useState('');
  const [assetSearch, setAssetSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<Dayjs | null>(null);
  const dateRangeInvalid = Boolean(dateFrom?.isValid() && dateTo?.isValid() && dateFrom.isAfter(dateTo, 'day'));
  const [sortField, setSortField] = useState<MaintenanceSortField | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const mappedLogs = useMemo(() => {
    return logs.map((log) => {
      const documentNumber =
        log.document_number ??
        (log.document_entity_identifier
          ? log.document_entity_identifier.replace(/^MX-/, '')
          : null);
      const derivedStatus = getDerivedStatus(log);
      return { ...log, documentNumber, derivedStatus };
    });
  }, [logs]);

  const filteredLogs = useMemo(() => {
    let result = mappedLogs;

    const query = documentSearch.trim().toLowerCase();
    if (query) {
      result = result.filter((log) => multiWordMatch(log.documentNumber ?? '', query));
    }

    const assetQuery = assetSearch.trim().toLowerCase();
    if (assetQuery) {
      result = result.filter((log) =>
        multiWordMatch(
          `${log.asset_names_agg ?? ''} ${log.lins_agg ?? ''} ${log.nsns_agg ?? ''}`,
          assetQuery,
        ),
      );
    }

    switch (statusFilter) {
      case 'pending':
        result = result.filter((log) => log.derivedStatus === 'Pending');
        break;
      case 'partially_received':
        result = result.filter((log) => log.derivedStatus === 'Partially Received');
        break;
      case 'complete':
        result = result.filter((log) => log.derivedStatus === 'Complete');
        break;
      case 'all':
      default:
        break;
    }

    if (dateRangeInvalid) return [];

    const from = dateFrom ? dateFrom.startOf('day').valueOf() : null;
    const to = dateTo ? dateTo.endOf('day').valueOf() : null;

    if (from !== null || to !== null) {
      result = result.filter((log) => {
        const sentAt = dayjs(log.date_sent);
        if (!sentAt.isValid()) {
          return false;
        }
        const sentAtValue = sentAt.valueOf();
        if (from !== null && sentAtValue < from) {
          return false;
        }
        if (to !== null && sentAtValue > to) {
          return false;
        }
        return true;
      });
    }

    return result;
  }, [mappedLogs, documentSearch, assetSearch, statusFilter, dateFrom, dateTo, dateRangeInvalid]);

  const sortedLogs = useMemo(() => {
    if (!sortField) return filteredLogs;
    return [...filteredLogs].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;
      switch (sortField) {
        case 'documentNumber':
          aVal = (a.documentNumber ?? '').toLowerCase();
          bVal = (b.documentNumber ?? '').toLowerCase();
          break;
        case 'date_sent':
          aVal = a.date_sent ?? '';
          bVal = b.date_sent ?? '';
          break;
        case 'total_quantity':
          aVal = a.total_quantity ?? 0;
          bVal = b.total_quantity ?? 0;
          break;
        case 'received_quantity':
          aVal = a.received_quantity ?? 0;
          bVal = b.received_quantity ?? 0;
          break;
        case 'derivedStatus': {
          const order: Record<DerivedStatus, number> = { 'Pending': 0, 'Partially Received': 1, 'Complete': 2 };
          aVal = order[a.derivedStatus] ?? 0;
          bVal = order[b.derivedStatus] ?? 0;
          break;
        }
        default:
          return 0;
      }
      if (aVal === bVal) return 0;
      const result = aVal < bVal ? -1 : 1;
      return sortDirection === 'asc' ? result : -result;
    });
  }, [filteredLogs, sortField, sortDirection]);

  const handleSort = useCallback((field: MaintenanceSortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField]);

  const maintenanceSendTransactionIds = useMemo(
    () => sortedLogs.map((log) => log.id),
    [sortedLogs],
  );
  const { itemsByLogId, isLoading: isLoadingLogItems } = useMaintenanceRequestLogItems(
    maintenanceSendTransactionIds,
  );

  const [expandedLogIds, setExpandedLogIds] = useState<Set<string>>(() => new Set());
  const toggleLogExpansion = useCallback((logId: string) => {
    setExpandedLogIds((prev) => {
      const next = new Set(prev);
      if (next.has(logId)) {
        next.delete(logId);
      } else {
        next.add(logId);
      }
      return next;
    });
  }, []);

  // Expand All / Collapse All handlers
  const handleExpandAll = useCallback(() => {
    setExpandedLogIds(new Set(sortedLogs.map((log) => log.id)));
  }, [sortedLogs]);

  const handleCollapseAll = useCallback(() => {
    setExpandedLogIds(new Set());
  }, []);

  const allExpanded = expandedLogIds.size === sortedLogs.length && sortedLogs.length > 0;
  const noneExpanded = expandedLogIds.size === 0;

  return (
    <Container maxWidth={false} sx={{ mt: 3, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <MUILink
            color="inherit"
            href="/"
            onClick={(event) => {
              event.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </MUILink>
          <Typography color="text.primary">Maintenance Request</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Build color="primary" sx={{ fontSize: 36 }} />
          <Typography variant="h4" component="h1">
            Maintenance Request
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1.5 }}>
          Send in-stock assets to maintenance and receive them back into stock.
        </Typography>
      </Box>

      <Paper elevation={1} sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                value={documentSearch}
                onChange={(event) => setDocumentSearch(event.target.value)}
                size="small"
                label="Document Number"
                placeholder="Search..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 220 }}
              />

              <TextField
                value={assetSearch}
                onChange={(event) => setAssetSearch(event.target.value)}
                size="small"
                label="Asset / LIN / NSN"
                placeholder="Search..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 220 }}
              />

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="maintenance-status-filter-label">Status</InputLabel>
                <Select
                  labelId="maintenance-status-filter-label"
                  id="maintenance-status-filter"
                  value={statusFilter}
                  label="Status"
                  onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="partially_received">Partially Received</MenuItem>
                  <MenuItem value="complete">Complete</MenuItem>
                </Select>
              </FormControl>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Date Sent From"
                  value={dateFrom}
                  onChange={(value) => setDateFrom(value?.isValid() ? value : null)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { minWidth: 180 },
                    },
                  }}
                />

                <DatePicker
                  label="Date Sent To"
                  value={dateTo}
                  onChange={(value) => setDateTo(value?.isValid() ? value : null)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { minWidth: 180 },
                    },
                  }}
                />
              </LocalizationProvider>

              {dateRangeInvalid && (
                <Alert severity="error">Date From cannot be after Date To.</Alert>
              )}

              {/* Expand All / Collapse All button */}
              {filteredLogs.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={allExpanded ? <UnfoldLess /> : <UnfoldMore />}
                  onClick={allExpanded ? handleCollapseAll : handleExpandAll}
                  disabled={sortedLogs.length === 0}
                >
                  {allExpanded ? 'Collapse All' : noneExpanded ? 'Expand All' : 'Expand All'}
                </Button>
              )}
            </Box>

            {!isViewer && (
            <Button variant="contained" disabled={isCartsLoading} onClick={() => void handleOpenNewSend()}>
              Send for Maintenance
            </Button>
            )}
          </Box>

          {savedMaintenanceCarts.length > 0 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>Saved Maintenance Carts</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small" aria-label="Saved maintenance carts">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Items</TableCell>
                      <TableCell>Date Created</TableCell>
                      <TableCell>Last Updated</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {savedMaintenanceCarts.map((cart) => (
                      <TableRow key={cart.id} hover>
                        <TableCell>
                          <Chip
                            size="small"
                            label={cart.workflow_type === 'maintenance-send' ? 'Send' : 'Receive'}
                            color={cart.workflow_type === 'maintenance-send' ? 'info' : 'success'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">{cart.item_count ?? 0}</TableCell>
                        <TableCell>
                          {dayjs(cart.created_at).isValid()
                            ? dayjs(cart.created_at).format('MM/DD/YYYY HH:mm')
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {dayjs(cart.updated_at).isValid()
                            ? dayjs(cart.updated_at).format('MM/DD/YYYY HH:mm')
                            : '—'}
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            {!isViewer && (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => {
                                  if (cart.workflow_type === 'maintenance-send') {
                                    setSendModalOpen(true);
                                  } else if (cart.workflow_type === 'maintenance-receive' && cart.maintenance_request_id) {
                                    setReceiveTarget({
                                      maintenanceRequestId: cart.maintenance_request_id,
                                      documentNumber: cart.document_number ?? null,
                                    });
                                  }
                                }}
                                disabled={
                                  cart.workflow_type === 'maintenance-send'
                                    ? false
                                    : !cart.maintenance_request_id
                                }
                              >
                                Resume
                              </Button>
                            )}
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              disabled={clearCartMutation.isPending}
                              onClick={() => setCancelCart(cart)}
                            >
                              Cancel
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {logsQuery.isError ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => void logsQuery.refetch()}>
                  Retry
                </Button>
              }
            >
              {getErrorMessage(logsQuery.error) || 'Failed to load maintenance requests.'}
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="medium" aria-label="Maintenance Requests">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 48 }} />
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'documentNumber'}
                        direction={sortField === 'documentNumber' ? sortDirection : 'asc'}
                        onClick={() => handleSort('documentNumber')}
                      >
                        Document Number
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'date_sent'}
                        direction={sortField === 'date_sent' ? sortDirection : 'asc'}
                        onClick={() => handleSort('date_sent')}
                      >
                        Date Sent
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortField === 'total_quantity'}
                        direction={sortField === 'total_quantity' ? sortDirection : 'asc'}
                        onClick={() => handleSort('total_quantity')}
                        sx={{ flexDirection: 'row' }}
                      >
                        Total Qty
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortField === 'received_quantity'}
                        direction={sortField === 'received_quantity' ? sortDirection : 'asc'}
                        onClick={() => handleSort('received_quantity')}
                        sx={{ flexDirection: 'row' }}
                      >
                        Qty Received
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'derivedStatus'}
                        direction={sortField === 'derivedStatus' ? sortDirection : 'asc'}
                        onClick={() => handleSort('derivedStatus')}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logsQuery.isLoading ? (
                    <TableRow>
                      <TableCell colSpan={7}>Loading...</TableCell>
                    </TableRow>
                  ) : sortedLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>No maintenance requests found</TableCell>
                    </TableRow>
                  ) : (
                    sortedLogs.map((log) => {
                      const isExpanded = expandedLogIds.has(log.id);
                      const items = itemsByLogId.get(log.id) ?? [];
                      const hasLineItems = log.total_quantity > 0;
                      const canReceive = !log.is_complete && log.outstanding_quantity > 0;

                      return (
                        <React.Fragment key={log.id}>
                          <TableRow
                            hover
                            sx={{
                              '& > *': {
                                borderBottom: isExpanded && hasLineItems ? 'unset' : undefined,
                              },
                              cursor: hasLineItems ? 'pointer' : 'default',
                            }}
                            onClick={() => hasLineItems && toggleLogExpansion(log.id)}
                          >
                            <TableCell sx={{ width: 48 }}>
                              {hasLineItems && (
                                <IconButton
                                  size="small"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    toggleLogExpansion(log.id);
                                  }}
                                  aria-label={isExpanded ? 'Collapse request' : 'Expand request'}
                                >
                                  {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                                </IconButton>
                              )}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 500 }}>
                              {log.documentNumber ?? '—'}
                            </TableCell>
                            <TableCell>
                              {dayjs(log.date_sent).isValid()
                                ? dayjs(log.date_sent).format('MM/DD/YYYY')
                                : '—'}
                            </TableCell>
                            <TableCell align="right">{log.total_quantity}</TableCell>
                            <TableCell align="right">
                              {log.received_quantity > 0 ? (
                                <Tooltip
                                  title={`${log.received_quantity} received, ${log.outstanding_quantity} outstanding`}
                                  arrow
                                >
                                  <Chip
                                    size="small"
                                    label={`${log.received_quantity} / ${log.total_quantity}`}
                                    color={log.is_complete ? 'success' : 'warning'}
                                    variant="outlined"
                                    sx={{ cursor: 'help' }}
                                  />
                                </Tooltip>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  0 / {log.total_quantity}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={log.derivedStatus}
                                color={
                                  log.derivedStatus === 'Complete'
                                    ? 'success'
                                    : log.derivedStatus === 'Partially Received'
                                      ? 'warning'
                                      : 'default'
                                }
                                variant={log.derivedStatus === 'Pending' ? 'outlined' : 'filled'}
                              />
                            </TableCell>
                            <TableCell align="right" onClick={(event) => event.stopPropagation()}>
                              {!isViewer && canReceive ? (
                                <Button
                                  size="small"
                                  startIcon={<Inventory />}
                                  onClick={() =>
                                    setReceiveTarget({
                                      maintenanceRequestId: log.id,
                                      documentNumber: log.documentNumber,
                                    })
                                  }
                                >
                                  Receive
                                </Button>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  -
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>

                          {hasLineItems && (
                            <TableRow>
                              <TableCell colSpan={7} sx={{ py: 0, bgcolor: 'action.hover' }}>
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                  <Box sx={{ py: 2, px: 4 }}>
                                    <Typography
                                      variant="subtitle2"
                                      gutterBottom
                                      component="div"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      Line Items
                                    </Typography>
                                    <Table size="small" aria-label="Request line items">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Asset Name</TableCell>
                                          <TableCell>LIN</TableCell>
                                          <TableCell>NSN</TableCell>
                                          <TableCell align="right">Qty Sent</TableCell>
                                          <TableCell align="right">Qty Received</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {isLoadingLogItems ? (
                                          <TableRow>
                                            <TableCell colSpan={5}>Loading...</TableCell>
                                          </TableRow>
                                        ) : items.length === 0 ? (
                                          <TableRow>
                                            <TableCell colSpan={5}>No line items found</TableCell>
                                          </TableRow>
                                        ) : (
                                          items.map((item) => {
                                            const isFullyReceived =
                                              item.received_quantity >= item.sent_quantity;
                                            const hasReceipts = item.received_quantity > 0;

                                            return (
                                              <TableRow key={item.id}>
                                                <TableCell>{item.asset_name ?? '—'}</TableCell>
                                                <TableCell>{item.lin ?? '—'}</TableCell>
                                                <TableCell>{item.nsn ?? '—'}</TableCell>
                                                <TableCell align="right">
                                                  {item.sent_quantity}
                                                </TableCell>
                                                <TableCell align="right">
                                                  {hasReceipts ? (
                                                    <Chip
                                                      size="small"
                                                      label={`${item.received_quantity} / ${item.sent_quantity}`}
                                                      color={
                                                        isFullyReceived ? 'success' : 'warning'
                                                      }
                                                      variant="outlined"
                                                    />
                                                  ) : (
                                                    <Typography
                                                      variant="body2"
                                                      color="text.secondary"
                                                    >
                                                      0 / {item.sent_quantity}
                                                    </Typography>
                                                  )}
                                                </TableCell>
                                              </TableRow>
                                            );
                                          })
                                        )}
                                      </TableBody>
                                    </Table>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </Paper>

      <Dialog
        open={newSendConflictOpen}
        onClose={() => !isResolvingNewSend && setNewSendConflictOpen(false)}
        aria-labelledby="new-maintenance-cart-title"
      >
        <DialogTitle id="new-maintenance-cart-title">Existing Saved Cart Found</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You already have a saved maintenance send cart. Resume it to continue where you left
            off, or cancel it to start a brand new cart.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setNewSendConflictOpen(false)}
            color="inherit"
            disabled={isResolvingNewSend}
          >
            Back
          </Button>
          <Button
            onClick={() => {
              setNewSendConflictOpen(false);
              setSendModalOpen(true);
            }}
            variant="outlined"
            disabled={isResolvingNewSend}
          >
            Resume Saved Cart
          </Button>
          <Button
            onClick={() => void handleStartNewSend()}
            color="error"
            variant="contained"
            disabled={isResolvingNewSend}
          >
            {isResolvingNewSend ? 'Starting New…' : 'Start New Cart'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancelCart !== null}
        onClose={() => !clearCartMutation.isPending && setCancelCart(null)}
        aria-labelledby="cancel-maintenance-cart-title"
      >
        <DialogTitle id="cancel-maintenance-cart-title">Cancel Saved Cart</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will release all reserved items in this maintenance cart. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelCart(null)} disabled={clearCartMutation.isPending}>
            Keep Cart
          </Button>
          <Button
            onClick={() => void handleCancelSavedCart()}
            color="error"
            variant="contained"
            disabled={clearCartMutation.isPending}
          >
            Cancel Cart
          </Button>
        </DialogActions>
      </Dialog>

      <SendForMaintenanceModal open={sendModalOpen} onClose={() => setSendModalOpen(false)} />
      <ReceiveFromMaintenanceModal
        open={Boolean(receiveTarget)}
        maintenanceRequestId={receiveTarget?.maintenanceRequestId ?? null}
        documentNumber={receiveTarget?.documentNumber ?? null}
        onClose={() => setReceiveTarget(null)}
      />
    </Container>
  );
};
